import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { quizActions } from '../actions/quiz.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class QuizEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getQuiz$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizActions.getQuiz),
            mergeMap(({ id }) => {
                return this.httpService.getQuiz(id).pipe(
                    finalizeLoader('quiz-get'),
                    map((quiz) => quizActions.getQuizSuccess({ payload: quiz })),
                    catchError((response: HttpErrorResponse) => of(quizActions.getQuizError({ code: response.status, id }))),
                );
            }),
        );
    });

    createQuiz$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizActions.createQuiz),
            mergeMap(({ value }) => {
                return this.httpService.createQuiz(value).pipe(
                    finalizeLoader('quiz-create'),
                    map((quiz) => quizActions.createQuizSuccess({ payload: quiz })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.quizzes.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.quizzes.createError');
                        return of(quizActions.createQuizError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateQuiz$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizActions.updateQuiz),
            mergeMap(({ id, value }) => {
                return this.httpService.updateQuiz(id, value).pipe(
                    finalizeLoader('quiz-update'),
                    map((quiz) => quizActions.updateQuizSuccess({ payload: quiz })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.quizzes.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.quizzes.updateError');
                        return of(quizActions.updateQuizError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteQuiz$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizActions.deleteQuiz),
            mergeMap(({ id }) => {
                return this.httpService.deleteQuiz(id).pipe(
                    finalizeLoader('quiz-delete'),
                    map(() => quizActions.deleteQuizSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.quizzes.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.quizzes.deleteError');
                        return of(quizActions.deleteQuizError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
